/**
 * TaskFlowTimelineView.tsx
 *
 * Interactive Timeline (Gantt) View with SVG Bézier Dependency Curves Layer.
 * Supports zoom levels (Day/Week/Month), progress percentage bar fills,
 * and dependency link rendering.
 */

import React, { useState, useMemo } from 'react';
import type { TaskFlowItem } from '../../../plugins/taskflow/contracts/taskflow-schema.js';
import {
  TimelineMathEngine,
  type TimelineZoomLevel,
  type BarDimensions,
} from '../../../plugins/taskflow/engine/taskflow-timeline-math.js';

export interface TaskFlowTimelineViewProps {
  workspaceId: string;
  items: TaskFlowItem[];
  onSelectTask: (task: TaskFlowItem) => void;
  selectedTaskId?: string | null;
}

const ROW_HEIGHT = 44;
const HEADER_HEIGHT = 36;
const BAR_HEIGHT = 24;

export const TaskFlowTimelineView: React.FC<TaskFlowTimelineViewProps> = ({
  workspaceId,
  items,
  onSelectTask,
  selectedTaskId,
}) => {
  const [zoomLevel, setZoomLevel] = useState<TimelineZoomLevel>('day');

  // Compute base start date (earliest task start date or today)
  const baseStartDate = useMemo(() => {
    let earliest = Date.now();
    for (const item of items) {
      const dateStr = item.startDate || item.scheduledDate || item.dueDate;
      if (dateStr) {
        const ms = new Date(dateStr).getTime();
        if (!isNaN(ms) && ms < earliest) {
          earliest = ms;
        }
      }
    }
    // Start at beginning of that month
    const d = new Date(earliest);
    d.setDate(1);
    return d.toISOString();
  }, [items]);

  // Map each item to row index and calculate bar dimensions
  const { rowData, barMap, totalWidth } = useMemo(() => {
    const rows = items.map((item, index) => {
      const coords = TimelineMathEngine.calculateBarCoordinates({
        startDate: item.startDate || item.scheduledDate,
        endDate: item.endDate,
        dueDate: item.dueDate,
        baseStartDate,
        zoomLevel,
      });

      const dimensions: BarDimensions = {
        x: coords.x,
        y: index * ROW_HEIGHT + (ROW_HEIGHT - BAR_HEIGHT) / 2,
        width: coords.width,
        height: BAR_HEIGHT,
      };

      return {
        item,
        index,
        dimensions,
      };
    });

    const bMap = new Map<string, BarDimensions>();
    let maxEndX = 1200;

    for (const r of rows) {
      bMap.set(r.item.id, r.dimensions);
      const endX = r.dimensions.x + r.dimensions.width + 200;
      if (endX > maxEndX) maxEndX = endX;
    }

    return { rowData: rows, barMap: bMap, totalWidth: maxEndX };
  }, [items, baseStartDate, zoomLevel]);

  // Compute SVG Bézier dependency paths
  const dependencyPaths = useMemo(() => {
    const paths: Array<{ id: string; d: string }> = [];

    for (const r of rowData) {
      if (r.item.relationships) {
        for (const rel of r.item.relationships) {
          if (rel.type === 'dependsOn' || rel.type === 'blockedBy') {
            // rel.targetId is the prerequisite, r.item.id is the dependent
            const prereqBar = barMap.get(rel.targetId);
            const dependentBar = r.dimensions;

            if (prereqBar && dependentBar) {
              const d = TimelineMathEngine.calculateDependencyBezier(prereqBar, dependentBar);
              paths.push({ id: `${rel.targetId}->${r.item.id}`, d });
            }
          }
        }
      }
    }

    return paths;
  }, [rowData, barMap]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        backgroundColor: 'var(--theme-bg-main, #0b0f19)',
        color: 'var(--theme-text-primary, #f8fafc)',
        overflow: 'hidden',
      }}
    >
      {/* Zoom Toolbar */}
      <div
        style={{
          padding: '8px 16px',
          borderBottom: '1px solid var(--theme-border-subtle, rgba(255, 255, 255, 0.08))',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: 'var(--theme-bg-header, rgba(15, 23, 42, 0.5))',
        }}
      >
        <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--theme-text-secondary, #94a3b8)' }}>
          Timeline Schedule & Gantt View
        </span>

        <div style={{ display: 'flex', gap: '4px', backgroundColor: 'var(--theme-bg-subtle, rgba(0, 0, 0, 0.3))', padding: '2px', borderRadius: '6px' }}>
          {(['day', 'week', 'month'] as TimelineZoomLevel[]).map((lvl) => (
            <button
              key={lvl}
              onClick={() => setZoomLevel(lvl)}
              style={{
                padding: '4px 10px',
                fontSize: '11px',
                fontWeight: 600,
                textTransform: 'capitalize',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: zoomLevel === lvl ? 'var(--theme-accent, #6366f1)' : 'transparent',
                color: zoomLevel === lvl ? '#fff' : 'var(--theme-text-muted, #94a3b8)',
                cursor: 'pointer',
              }}
            >
              {lvl}
            </button>
          ))}
        </div>
      </div>

      {/* Main Split Layout: Left Table Rows, Right Horizontal Canvas */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left Sticky Column: Task Titles */}
        <div
          style={{
            width: '280px',
            borderRight: '1px solid var(--theme-border, rgba(255, 255, 255, 0.1))',
            backgroundColor: 'var(--theme-bg-sidebar, rgba(15, 23, 42, 0.7))',
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
            overflowY: 'auto',
          }}
        >
          <div
            style={{
              height: `${HEADER_HEIGHT}px`,
              padding: '0 16px',
              display: 'flex',
              alignItems: 'center',
              fontSize: '11px',
              fontWeight: 700,
              textTransform: 'uppercase',
              color: 'var(--theme-text-secondary, #94a3b8)',
              borderBottom: '1px solid var(--theme-border-subtle, rgba(255, 255, 255, 0.08))',
            }}
          >
            Tasks ({items.length})
          </div>

          {rowData.map((row) => (
            <div
              key={row.item.id}
              onClick={() => onSelectTask(row.item)}
              style={{
                height: `${ROW_HEIGHT}px`,
                padding: '0 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid var(--theme-border-subtle, rgba(255, 255, 255, 0.04))',
                backgroundColor: selectedTaskId === row.item.id ? 'var(--theme-accent-subtle, rgba(99, 102, 241, 0.15))' : 'transparent',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 600,
                userSelect: 'none',
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {row.item.title}
              </span>
              <span
                style={{
                  fontSize: '10px',
                  color: 'var(--theme-text-muted, #64748b)',
                  backgroundColor: 'var(--theme-bg-subtle, rgba(255, 255, 255, 0.05))',
                  padding: '1px 5px',
                  borderRadius: '3px',
                }}
              >
                {row.item.type}
              </span>
            </div>
          ))}
        </div>

        {/* Right Scrollable Canvas with Gantt Bars and SVG Bézier Overlay */}
        <div style={{ flex: 1, overflow: 'auto', position: 'relative' }}>
          <div
            style={{
              width: `${totalWidth}px`,
              minHeight: '100%',
              position: 'relative',
            }}
          >
            {/* Timeline Header Row */}
            <div
              style={{
                height: `${HEADER_HEIGHT}px`,
                borderBottom: '1px solid var(--theme-border-subtle, rgba(255, 255, 255, 0.08))',
                backgroundColor: 'var(--theme-bg-header, rgba(15, 23, 42, 0.4))',
                position: 'sticky',
                top: 0,
                zIndex: 10,
              }}
            />

            {/* Grid Row Backgrounds */}
            {rowData.map((row) => (
              <div
                key={row.item.id}
                style={{
                  height: `${ROW_HEIGHT}px`,
                  borderBottom: '1px solid var(--theme-border-subtle, rgba(255, 255, 255, 0.03))',
                  backgroundColor: selectedTaskId === row.item.id ? 'var(--theme-accent-subtle, rgba(99, 102, 241, 0.08))' : 'transparent',
                }}
              />
            ))}

            {/* SVG Dependency Bézier Curves Overlay */}
            <svg
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${rowData.length * ROW_HEIGHT + HEADER_HEIGHT}px`,
                pointerEvents: 'none',
                zIndex: 5,
              }}
            >
              <defs>
                <marker
                  id="arrow"
                  viewBox="0 0 10 10"
                  refX="6"
                  refY="5"
                  markerWidth="6"
                  markerHeight="6"
                  orient="auto-start-reverse"
                >
                  <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--theme-accent, #818cf8)" />
                </marker>
              </defs>
              {dependencyPaths.map((p) => (
                <path
                  key={p.id}
                  d={p.d}
                  fill="none"
                  stroke="var(--theme-accent, #818cf8)"
                  strokeWidth="2"
                  strokeDasharray="4 2"
                  markerEnd="url(#arrow)"
                  opacity="0.8"
                />
              ))}
            </svg>

            {/* Render Gantt Bars */}
            {rowData.map((row) => {
              const { dimensions, item } = row;
              const isSelected = selectedTaskId === item.id;
              const progress = item.progressPercentage || 0;

              return (
                <div
                  key={item.id}
                  onClick={() => onSelectTask(item)}
                  style={{
                    position: 'absolute',
                    left: `${dimensions.x}px`,
                    top: `${dimensions.y + HEADER_HEIGHT}px`,
                    width: `${Math.max(24, dimensions.width)}px`,
                    height: `${dimensions.height}px`,
                    backgroundColor: isSelected
                      ? 'var(--theme-accent, #6366f1)'
                      : 'var(--theme-bg-card, rgba(30, 41, 59, 0.9))',
                    border: isSelected
                      ? '1px solid #ffffff'
                      : '1px solid var(--theme-border, rgba(255, 255, 255, 0.15))',
                    borderRadius: '6px',
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
                    cursor: 'pointer',
                    zIndex: 6,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 8px',
                    boxSizing: 'border-box',
                    transition: 'box-shadow 0.15s ease',
                  }}
                  title={`${item.title} (${progress}%)`}
                >
                  {/* Progress Fill Background */}
                  {progress > 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: `${progress}%`,
                        backgroundColor: item.status === 'done'
                          ? 'var(--theme-status-success-bg, rgba(52, 211, 153, 0.3))'
                          : 'var(--theme-accent-subtle, rgba(99, 102, 241, 0.35))',
                        zIndex: -1,
                      }}
                    />
                  )}

                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: 'var(--theme-text-primary, #fff)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.title}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
